import { Spinner } from '../../components/Spinner.js';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { SpotCategory } from '@ridehub/shared';
import { getCategoryLabel, calculateStarScore, CATEGORY_MAP } from '@ridehub/shared';
import { StarRating } from '../../components/StarRating.js';
import { useGeolocationContext } from '../../contexts/GeolocationContext.js';
import { SpotFilterPanel } from './SpotFilterPanel.js';
import { useSpotSearch } from './useSpotSearch.js';

const DEFAULT_LAT = 35.6762;
const DEFAULT_LNG = 139.6503;
const MAP_STATE_KEY = 'ridehub_map_state';

function loadSavedMapBbox():
  | { minLat: number; maxLat: number; minLng: number; maxLng: number }
  | undefined {
  try {
    const saved = sessionStorage.getItem(MAP_STATE_KEY);
    if (!saved) return undefined;
    const state = JSON.parse(saved) as {
      bbox?: { minLat: number; maxLat: number; minLng: number; maxLng: number };
    };
    return state.bbox;
  } catch {
    return undefined;
  }
}

type SortOrder = 'distance' | 'likes' | 'checkins' | 'rank';

function formatDistance(meters?: number): string {
  if (meters === undefined) return '';
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

export function SpotListView() {
  const [searchParams] = useSearchParams();
  const { latitude, longitude } = useGeolocationContext();
  const [category, setCategory] = useState<SpotCategory | undefined>();
  const [sort, setSort] = useState<SortOrder>('distance');

  // マップから遷移した場合はその中心座標・bbox を使用、なければ現在地・デフォルト半径
  const paramLat = searchParams.get('lat');
  const paramLng = searchParams.get('lng');
  const paramRadius = searchParams.get('radius');
  const lat = paramLat ? Number(paramLat) : (latitude ?? DEFAULT_LAT);
  const lng = paramLng ? Number(paramLng) : (longitude ?? DEFAULT_LNG);
  const radius = paramRadius ? Number(paramRadius) : undefined;

  // マップからbboxパラメータが渡された場合はbbox検索を使用
  // なければsessionStorageに保存された直近のマップ表示範囲を使用
  const paramMinLat = searchParams.get('minLat');
  const paramMaxLat = searchParams.get('maxLat');
  const paramMinLng = searchParams.get('minLng');
  const paramMaxLng = searchParams.get('maxLng');
  const urlBbox =
    paramMinLat && paramMaxLat && paramMinLng && paramMaxLng
      ? {
          minLat: Number(paramMinLat),
          maxLat: Number(paramMaxLat),
          minLng: Number(paramMinLng),
          maxLng: Number(paramMaxLng),
        }
      : undefined;
  const bbox = urlBbox ?? loadSavedMapBbox();

  const { spots, loading, error } = useSpotSearch({
    lat,
    lng,
    radius,
    bbox,
    category,
    sort,
  });

  return (
    <div className="flex flex-col h-screen">
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <h1 className="text-lg font-bold">スポット一覧</h1>
      </div>

      <SpotFilterPanel
        category={category}
        sort={sort}
        onCategoryChange={setCategory}
        onSortChange={setSort}
      />

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex justify-center py-8">
            <Spinner size="sm" />
          </div>
        )}
        {error && <p className="text-center text-red-500 py-8">{error}</p>}
        {!loading && spots.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            <p className="text-sm">スポットが見つかりません</p>
          </div>
        )}
        <ul>
          {spots.map((spot) => {
            const catDef = CATEGORY_MAP.get(spot.category as SpotCategory);
            const accentColor = catDef?.color ?? '#E0E0E0';
            const iconBg = `${accentColor}33`;
            return (
              <li key={spot.id} className="border-b border-gray-100 last:border-0">
                <Link
                  to={`/spots/${spot.id}`}
                  className="flex items-start gap-3 px-4 py-3.5 hover:bg-stone-50 border-l-[3px]"
                  style={{ borderLeftColor: accentColor }}
                >
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                    style={{ backgroundColor: iconBg }}
                  >
                    {catDef?.icon ?? '📍'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold text-gray-900 truncate">{spot.name}</h2>
                      <StarRating score={calculateStarScore(spot.rankScore)} variant="compact" />
                    </div>
                    <p className="text-xs text-gray-400 truncate mt-0.5">
                      {getCategoryLabel(spot.category as SpotCategory)}
                      {spot.address && ` · ${spot.address}`}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="flex-shrink-0"
                        >
                          <ellipse cx="12" cy="17.5" rx="5.5" ry="3" />
                          <ellipse cx="5.5" cy="12" rx="2" ry="2.5" />
                          <ellipse cx="9.5" cy="9" rx="2" ry="2.5" />
                          <ellipse cx="14.5" cy="9" rx="2" ry="2.5" />
                          <ellipse cx="18.5" cy="12" rx="2" ry="2.5" />
                        </svg>
                        {spot.totalCheckins}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="flex-shrink-0"
                        >
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        {spot.totalLikes}
                      </span>
                      {spot.distance !== undefined && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="flex-shrink-0"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" />
                          </svg>
                          {formatDistance(spot.distance)}
                        </span>
                      )}
                    </div>
                    {spot.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {spot.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {spot.tags.length > 3 && (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                            +{spot.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
